import { useMemo, useState } from 'react';
import {
  TEMPLATE_FIELD_FILL_BY,
  TEMPLATE_FIELD_TYPES,
  type DocumentRenderSection,
  type TemplateField,
  type TemplateFieldFillBy,
  type TemplateFieldPlacement,
  type TemplateFieldType,
  type TemplateStatus,
} from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { assertToolSucceeded } from '@/api/templates-api';
import { useUpdateTemplateSection } from '@/hooks/useTemplateQueries';
import { cleanCell, listBodyTokens, parseSectionTables } from './section-tables';
import { toolErrorText } from '@/utils/tool-error';

interface TemplateFieldsEditorProps {
  section: DocumentRenderSection;
  templateStatus: TemplateStatus;
}

interface PlacementCandidate {
  id: string;
  label: string;
  placement: TemplateFieldPlacement;
}

interface FieldDraft {
  originalKey: string | null;
  key: string;
  keyEdited: boolean;
  label: string;
  type: TemplateFieldType;
  fillBy: TemplateFieldFillBy;
  required: boolean;
  options: string;
  allowOther: boolean;
  placementId: string;
}

const FILL_BY_LABELS: Record<TemplateFieldFillBy, string> = {
  member: 'Member',
  writer: 'Writer',
  either: 'Either',
};

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function placementId(placement: TemplateFieldPlacement): string {
  switch (placement.kind) {
    case 'token':
      return `token:${placement.find}`;
    case 'labelValue':
      return `labelValue:${placement.rowLabel}`;
    case 'column':
      return `column:${placement.tableBlockId}:${placement.column}`;
  }
}

function placementLabel(placement: TemplateFieldPlacement): string {
  switch (placement.kind) {
    case 'token':
      return `Token ${placement.find}`;
    case 'labelValue':
      return `Row '${placement.rowLabel}'`;
    case 'column':
      return `Column '${placement.column}' (table ${placement.tableBlockId})`;
  }
}

function emptyDraft(): FieldDraft {
  return {
    originalKey: null,
    key: '',
    keyEdited: false,
    label: '',
    type: 'text',
    fillBy: 'either',
    required: false,
    options: '',
    allowOther: false,
    placementId: '',
  };
}

function draftFrom(field: TemplateField): FieldDraft {
  return {
    originalKey: field.key,
    key: field.key,
    keyEdited: true,
    label: field.label,
    type: field.type,
    fillBy: field.fillBy,
    required: field.required === true,
    options: (field.options ?? []).join('\n'),
    allowOther: field.allowOther === true,
    placementId: placementId(field.placement),
  };
}

// Candidates are what the page can see in the body; the tool decides whether a placement resolves.
function collectCandidates(section: DocumentRenderSection): PlacementCandidate[] {
  const facts = section.templateSection;
  const candidates: PlacementCandidate[] = [];
  const push = (placement: TemplateFieldPlacement) => {
    const id = placementId(placement);
    if (!candidates.some((entry) => entry.id === id)) {
      candidates.push({ id, label: placementLabel(placement), placement });
    }
  };
  for (const find of listBodyTokens(section.description)) push({ kind: 'token', find });
  const tables = parseSectionTables(section.description);
  tables.forEach((table, index) => {
    const fact = facts?.tables[index];
    if (fact !== undefined && fact.mode === 'repeat') {
      const photo = fact.photoColumn === null ? null : cleanCell(fact.photoColumn);
      for (const header of table.header.map(cleanCell)) {
        if (header.length === 0 || header === photo) continue;
        push({ kind: 'column', tableBlockId: fact.blockId, column: header });
      }
      return;
    }
    for (const row of table.rows) {
      const rowLabel = cleanCell(row[0] ?? '');
      if (rowLabel.length > 0 && row.length >= 2 && !/^\[[^[\]]+\]$/.test(rowLabel)) {
        push({ kind: 'labelValue', rowLabel });
      }
    }
  });
  return candidates;
}

export function TemplateFieldsEditor({ section, templateStatus }: TemplateFieldsEditorProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FieldDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const updateSection = useUpdateTemplateSection();
  const facts = section.templateSection;
  const candidates = useMemo(() => collectCandidates(section), [section]);

  if (facts === null || templateStatus === 'RETIRED') return null;
  const fields = facts.fields;

  const writeFields = async (next: TemplateField[]) => {
    try {
      assertToolSucceeded(await updateSection.mutateAsync({ sectionDocumentId: section.id, fields: next }));
      setDraft(null);
      setError(null);
    } catch (caught) {
      setError(toolErrorText(caught));
    }
  };

  const draftCandidates: PlacementCandidate[] = (() => {
    if (draft?.originalKey === null || draft === null) return candidates;
    const current = fields.find((field) => field.key === draft.originalKey);
    if (current === undefined || candidates.some((entry) => entry.id === placementId(current.placement))) {
      return candidates;
    }
    return [
      { id: placementId(current.placement), label: `${placementLabel(current.placement)} (current)`, placement: current.placement },
      ...candidates,
    ];
  })();

  const saveDraft = async () => {
    if (draft === null) return;
    const placement = draftCandidates.find((entry) => entry.id === draft.placementId)?.placement;
    if (placement === undefined) return;
    const options = draft.options
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const field: TemplateField = {
      key: draft.key.trim(),
      label: draft.label.trim(),
      type: draft.type,
      fillBy: draft.fillBy,
      ...(draft.required ? { required: true } : {}),
      ...(draft.type === 'select' ? { options } : {}),
      ...(draft.type === 'select' && draft.allowOther ? { allowOther: true } : {}),
      placement,
    };
    const next =
      draft.originalKey === null
        ? [...fields.map(stripFilled), field]
        : fields.map((entry) => (entry.key === draft.originalKey ? field : stripFilled(entry)));
    await writeFields(next);
  };

  const removeField = async (key: string) => {
    await writeFields(fields.filter((field) => field.key !== key).map(stripFilled));
  };

  const canSaveDraft =
    draft !== null && draft.key.trim().length > 0 && draft.label.trim().length > 0 && draft.placementId.length > 0;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}>
        <Badge variant="outline">Fields · {fields.length}</Badge>
      </button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setDraft(null);
            setError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fields</DialogTitle>
            <DialogDescription>
              Typed values this section collects. Each field points at a token, a label/value row or a repeat-table column of the section.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            {draft === null ? (
              <>
                {fields.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No fields yet.</p>
                ) : (
                  <ul className="divide-y rounded-md border text-sm">
                    {fields.map((field) => (
                      <li key={field.key} className="flex flex-wrap items-center gap-2 px-3 py-2">
                        <span className="font-medium">{field.label}</span>
                        <code className="text-xs text-muted-foreground">{field.key}</code>
                        <Badge variant="secondary">{field.type}</Badge>
                        <Badge variant="secondary">{FILL_BY_LABELS[field.fillBy]}</Badge>
                        {field.required === true ? <Badge variant="outline">required</Badge> : null}
                        {field.type === 'select' ? (
                          <Badge variant="outline">{(field.options ?? []).length} option(s)</Badge>
                        ) : null}
                        <span className="ml-auto flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setDraft(draftFrom(field))}>
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void removeField(field.key)}
                            disabled={updateSection.isPending}
                          >
                            Remove
                          </Button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <Button size="sm" variant="outline" onClick={() => setDraft(emptyDraft())}>
                  Add field
                </Button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="field-label">Label</Label>
                    <Input
                      id="field-label"
                      value={draft.label}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          label: event.target.value,
                          key: draft.keyEdited ? draft.key : slugify(event.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="field-key">Key</Label>
                    <Input
                      id="field-key"
                      value={draft.key}
                      onChange={(event) => setDraft({ ...draft, key: event.target.value, keyEdited: true })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Type</Label>
                    <Select
                      value={draft.type}
                      onValueChange={(value) => setDraft({ ...draft, type: value as TemplateFieldType })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_FIELD_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Filled by</Label>
                    <Select
                      value={draft.fillBy}
                      onValueChange={(value) => setDraft({ ...draft, fillBy: value as TemplateFieldFillBy })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_FIELD_FILL_BY.map((fillBy) => (
                          <SelectItem key={fillBy} value={fillBy}>
                            {FILL_BY_LABELS[fillBy]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Placement</Label>
                  <Select value={draft.placementId} onValueChange={(value) => setDraft({ ...draft, placementId: value })}>
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder={draftCandidates.length > 0 ? 'Choose where the value goes…' : 'Nothing in this section to point at'} />
                    </SelectTrigger>
                    <SelectContent>
                      {draftCandidates.map((candidate) => (
                        <SelectItem key={candidate.id} value={candidate.id}>
                          {candidate.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="field-required"
                    checked={draft.required}
                    onCheckedChange={(checked) => setDraft({ ...draft, required: checked })}
                  />
                  <Label htmlFor="field-required">Required</Label>
                </div>
                {draft.type === 'select' ? (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="field-options">Options (one per line)</Label>
                      <Textarea
                        id="field-options"
                        rows={4}
                        value={draft.options}
                        onChange={(event) => setDraft({ ...draft, options: event.target.value })}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="field-allow-other"
                        checked={draft.allowOther}
                        onCheckedChange={(checked) => setDraft({ ...draft, allowOther: checked })}
                      />
                      <Label htmlFor="field-allow-other">Allow other</Label>
                    </div>
                  </>
                ) : null}
              </div>
            )}
            {error !== null ? <p className="text-sm text-destructive">{error}</p> : null}
          </DialogBody>
          {draft !== null ? (
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={updateSection.isPending}>
                Back
              </Button>
              <Button onClick={() => void saveDraft()} disabled={!canSaveDraft || updateSection.isPending}>
                Save
              </Button>
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

// The render payload decorates each field with its fill state; the tool takes the field alone.
function stripFilled(field: TemplateField & { filled?: boolean }): TemplateField {
  const { filled: _filled, ...rest } = field;
  return rest;
}
